import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { withStyles } from '@material-ui/core/styles';
import withResponsive from 'components/Layout/withResponsive';
import Select from 'components/forms/Select';
import Add from '@material-ui/icons/Add';
import Button from 'components/buttons/Button';
import { isEmpty } from 'lodash';
import Input from 'components/forms/Input';
import FilterIcon from 'icons/filter';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';
import Dialog from 'components/modals/Dialog';
import TrashIcon from 'icons/trash';
import Popover from 'components/Popover/Popover';
import classNames from 'classnames';

const EMPTY_FILTER = 'EMPTY';
const EMPTY_CONFIG = {
  key: 'empty',
  component: <Input
    placeholder="Set value"
    disabled
  />,
  menuLabel: null,
  onFilterMount: () => {},
};


const styles = theme => ({
  filterWrapper: {
    position: 'relative',
  },
  filterBtn: {
    height: '3rem',
    fontFamily: theme.typography.fontFamily,
    padding: '0.063rem 0.813rem 0.063rem 0.563rem',
    border: `0.063rem solid ${theme.palette.secondary['300']}`,
    borderRadius: '0.375rem',
    color: theme.palette.gray.contrastText,
    backgroundColor: theme.palette.white,
    fontSize: '0.875rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    '&:hover, &$opened': {
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.primary['100'],
      border: `1px solid ${theme.palette.primary.main}`,
      '& $filterIcon': {
        fill: theme.palette.primary.main,
      },
    },
  },
  filterIcon: {
    width: '1.063rem',
    height: '1.063rem',
    marginRight: '0.563rem',
    fill: theme.palette.secondary['300'],
  },
  isMobile: {},
  menuWrapper: {
    backgroundColor: theme.palette.white,
    width: '32.313rem',
    maxWidth: '100%',
    padding: '1rem',
    outline: 'none',
    '&$isMobile': {
      marginTop: '4rem',
      '& $header': {
        display: 'flex',
      },
      '& $filtersGrid': {
        gridTemplateColumns: '95%',
      },
      '& $valueDropdownWrapper': {
        marginBottom: '1.5rem',
      },
    },
  },
  header: {
    display: 'none',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    padding: '1rem',
    borderBottom: `1px solid ${theme.palette.secondary['100']}`,
  },
  headerTitle: {
    color: theme.palette.secondary.text,
    fontSize: '0.875rem',
    fontWeight: 600,
  },
  headerCloseIcon: {
    color: theme.palette.secondary['300'],
  },
  filterMenuTitle: {
    color: theme.palette.secondary['900'],
    fontFamily: theme.typography.fontFamily,
    fontSize: '0.875rem',
    fontWeight: '400',
  },
  filtersGrid: {
    display: 'grid',
    gridTemplateColumns: '10.5rem 16.813rem',
    gridGap: '1rem',
    marginTop: '1rem',
  },
  criteriaGrid: {
    display: 'grid',
    gridTemplateColumns: '5rem 10.80rem',
    gridGap: '1rem',
  },
  valueDropdownWrapper: {
    position: 'relative',
  },
  closeIconRoot: {
    position: 'absolute',
    top: '0.75rem',
    right: '-2.25rem',
    cursor: 'pointer',
    color: theme.palette.secondary['300'],
    '&:hover': {
      color: theme.palette.accent3['800'],
    },
  },
  btnWrapper: {
    maxWidth: '27.813rem',
    marginTop: '0.625rem',
  },
  resetLink: {
    float: 'right',
  },
  opened: {},
});

class DynamicFilter extends Component {
  constructor(props) {
    super(props);
    this.state = {
      anchorFiltersMenu: null,
      visibleFilters: this.getDefaultFilters(),
    };
  }

  onChangeFilterSelect = selectedFilter => (option) => {
    const newState = this.state;
    const selectedFilterIndex = newState.visibleFilters.findIndex(filter => filter === selectedFilter);
    newState.visibleFilters[selectedFilterIndex] = option.value;
    if (this.getFilterOptionConfig(option.value).onFilterMount) {
      this.getFilterOptionConfig(option.value)
        .onFilterMount(option.value, selectedFilter !== 'EMPTY' ? selectedFilter : null);
    } else if (selectedFilter !== 'EMPTY') {
      this.props.onResetSelectedFilter(selectedFilter);
    }
    this.setState(newState);
  }

  onDeleteFilter = selectedFilter => () => {
    const newState = this.state;
    const selectedFilterIndex = newState.visibleFilters.findIndex(filter => filter === selectedFilter);
    newState.visibleFilters.splice(selectedFilterIndex, 1);
    this.setState(newState);
    this.getFilterOptionConfig(selectedFilter).onDeleteFilter(selectedFilter);
  }

  onResetFilters = () => {
    const { onResetFilters } = this.props;
    this.setState({ visibleFilters: [EMPTY_FILTER] }, onResetFilters());
    this.handleFiltersMenuClose();
  }

  getDefaultFilters = () => {
    const options = this.props.options
      .filter(o => o.isDefault)
      .map(o => o.key);
    if (isEmpty(options)) {
      return [EMPTY_FILTER];
    }
    return options;
  };

  getFilterMenuValue = filter => (filter === EMPTY_FILTER ? null : {
    label: this.getFilterOptionConfig(filter).menuLabel,
    value: filter,
  })

  getFilterOptionConfig = (filter) => {
    const config = this.props.options.find(o => o.key === filter);
    if (config) {
      return config;
    }
    return EMPTY_CONFIG;
  }

  getUnselectedFilters = () => this.props.options
    .filter(o => !this.state.visibleFilters.includes(o.key))
    .map(o => o.key);

  getFilterBtnLabel = () => {
    const { visibleFilters } = this.state;
    let label = '';
    if (this.props.options.length === this.getUnselectedFilters().length) {
      label = 'Filter by';
    } else {
      label = `${visibleFilters.length} Filter`;
      label += visibleFilters.length > 1 ? 's' : '';
    }
    return label;
  }

  handleFilterMenuClick = (event) => {
    this.setState({ anchorFiltersMenu: event.currentTarget });
  }

  handleFiltersMenuClose = () => {
    this.setState({ anchorFiltersMenu: null });
  }

  addFilter = () => {
    const { visibleFilters } = this.state;
    visibleFilters.push(EMPTY_FILTER);
    this.setState({ visibleFilters });
  }

  buildOptions = optionKeys => optionKeys.map((o) => {
    const optionConfig = this.getFilterOptionConfig(o);
    return {
      label: optionConfig.menuLabel,
      value: o,
    };
  })

  render() {
    const { classes, isMobile, menuDescription } = this.props;
    const {
      visibleFilters,
      anchorFiltersMenu,
    } = this.state;

    const emptyFilter = visibleFilters.filter(filter => filter === EMPTY_FILTER);
    const unselectedFilters = this.getUnselectedFilters();
    const filterBtnLabel = this.getFilterBtnLabel();

    const popoverProps = {
      disableEnforceFocus: true,
      id: 'filters-menu',
      anchorEl: anchorFiltersMenu,
      open: Boolean(anchorFiltersMenu),
      onClose: this.handleFiltersMenuClose,
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'left',
      },
      transformOrigin: {
        vertical: 'top',
        horizontal: 'left',
      },
    };

    const dialogProps = {
      disableEnforceFocus: true,
      open: Boolean(anchorFiltersMenu),
      onBackdropClick: this.handleFiltersMenuClose,
      withPadding: false,
    };

    const MenuWrapper = isMobile ? Dialog : Popover;
    const menuWrapperProps = isMobile ? dialogProps : popoverProps;

    return (
      <div className={classes.filterWrapper}>
        <button
          className={classNames(classes.filterBtn, { [classes.opened]: anchorFiltersMenu })}
          onClick={this.handleFilterMenuClick}
        >
          <FilterIcon className={classes.filterIcon} viewBox="0 0 17 17" />
          {filterBtnLabel}
        </button>
        <MenuWrapper {...menuWrapperProps}>
          <div className={classNames(classes.menuWrapper, { [classes.ismobile]: isMobile })}>
            <div className={classes.header}>
              <div className={classes.headerTitle}>Filter by</div>
              <CloseRoundedIcon className={classes.headerCloseIcon} />
            </div>
            <div className={classes.filterMenuTitle}>{menuDescription}</div>
            <div className={classes.filtersGrid}>
              {visibleFilters.map(filter => (
                <React.Fragment>
                  <Select
                    placeholder="Select a filter"
                    onChange={this.onChangeFilterSelect(filter)}
                    options={this.buildOptions(unselectedFilters)}
                    value={this.getFilterMenuValue(filter)}
                  />
                  <div className={`${classes.valueDropdownWrapper}
                   ${this.getFilterOptionConfig(filter).filterCriteria ? classes.criteriaGrid : ''}`}
                  >
                    {this.getFilterOptionConfig(filter).filterCriteria}
                    {this.getFilterOptionConfig(filter).component}
                    <TrashIcon classes={{ root: classes.closeIconRoot }} onClick={this.onDeleteFilter(filter)} />
                  </div>
                </React.Fragment>
              ))}
            </div>
            <div className={classes.btnWrapper}>
              <Button
                color="secondary"
                variant="text"
                size="small"
                onClick={this.addFilter}
                startIcon={<Add />}
                disabled={!isEmpty(emptyFilter) || isEmpty(unselectedFilters)}
              >
                Add Filter
              </Button>
              {!isEmpty(visibleFilters) &&
                <Button
                  className={classes.resetLink}
                  color="primary"
                  variant="text"
                  size="small"
                  onClick={this.onResetFilters}
                >
                  Reset Filters
                </Button>
              }
            </div>
          </div>
        </MenuWrapper>
      </div>
    );
  }
}

DynamicFilter.propTypes = {

  // Idea config
  menuDescription: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({
    component: PropTypes.element.isRequired,
    menuLabel: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  })).isRequired,
  initialFilters: PropTypes.arrayOf(PropTypes.string),
  filterParams: PropTypes.shape({}).isRequired,
  filterParamsState: PropTypes.shape({}).isRequired,
  onResetFilters: PropTypes.func,
  onDeleteFilter: PropTypes.func,
  onResetSelectedFilter: PropTypes.func,

  // inhertited
  isMobile: PropTypes.bool.isRequired,
  classes: PropTypes.shape({}).isRequired,
};

DynamicFilter.defaultProps = {
  onResetFilters: () => {},
  onDeleteFilter: () => {},
  onResetSelectedFilter: () => {},
  initialFilters: [EMPTY_FILTER],
  menuDescription: 'Show all products where:',
};

export default compose(
  withStyles(styles, { withTheme: true }),
  withResponsive,
  connect(
    state => ({
      user: state.user,
      campaign: state.campaign,
    }),
    { },
  ),
)(DynamicFilter);
